import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import Heading from '../../components/header';
import useApiLoadingOverlay from '../../hooks/useApiLoadingOverlay';
import useApiTopLoading from '../../hooks/useApiTopLoading';
import SiteFaqContainer from '../../container/siteFaqContainer';
import {
    useCreateFaqMutation,
    useDeleteFaqMutation,
    useGetFaqBySectionQuery,
    useGetFaqListQuery,
    useLazyGetFaqByIdQuery,
    useUpdateFaqMutation,
} from './faqApiSlice';
import { actionNotifier } from '../../components/ui/toast';

const getFaqRows = (payload) => {
    if (Array.isArray(payload)) {
        return payload;
    }

    if (Array.isArray(payload?.data)) {
        return payload.data;
    }

    if (Array.isArray(payload?.rows)) {
        return payload.rows;
    }

    if (Array.isArray(payload?.data?.rows)) {
        return payload.data.rows;
    }

    if (Array.isArray(payload?.faq)) {
        return payload.faq;
    }

    if (Array.isArray(payload?.data?.faq)) {
        return payload.data.faq;
    }

    return [];
};

const getFaqDetail = (payload) => {
    if (!payload) {
        return null;
    }

    if (payload?.data && !Array.isArray(payload.data)) {
        return payload.data;
    }

    return payload;
};

const SiteFaqPage = () => {
    const dispatch = useDispatch();
    const [search, setSearch] = useState('');
    const [selectedSection, setSelectedSection] = useState('');

    const {
        data: faqListData,
        isLoading: isFaqListLoading,
        isFetching: isFaqListFetching,
        refetch: refetchFaqList,
    } = useGetFaqListQuery();

    const {
        data: faqBySectionData,
        isFetching: isFaqBySectionFetching,
    } = useGetFaqBySectionQuery(selectedSection, {
        skip: !selectedSection,
    });

    const [createFaq, { isLoading: isCreatingFaq }] = useCreateFaqMutation();
    const [updateFaq, { isLoading: isUpdatingFaq }] = useUpdateFaqMutation();
    const [deleteFaq, { isLoading: isDeletingFaq }] = useDeleteFaqMutation();
    const [fetchFaqById, { isLoading: isFetchingFaqById }] = useLazyGetFaqByIdQuery();

    const faqList = getFaqRows(faqListData);
    const sectionFaqList = selectedSection ? getFaqRows(faqBySectionData) : faqList;
    const sectionOptions = [...new Set(faqList.map((item) => item?.section).filter(Boolean))];

    useApiTopLoading([isFaqListFetching, isFaqBySectionFetching], dispatch);
    useApiLoadingOverlay(
        [isFaqListLoading, isCreatingFaq, isUpdatingFaq, isDeletingFaq, isFetchingFaqById],
        dispatch
    );

    useEffect(() => {
        if (selectedSection && sectionOptions.length > 0 && !sectionOptions.includes(selectedSection)) {
            setSelectedSection('');
        }
    }, [selectedSection, sectionOptions]);

    const onSearch = (event) => {
        setSearch(event.target.value);
    };

    const handleSaveFaq = async (faqData) => {
        const payload = {
            section: faqData.section.trim(),
            category: faqData.category.trim(),
            question: faqData.question.trim(),
            answer: faqData.answer.trim(),
            sortOrder: Number(faqData.sortOrder) || 1,
        };

        if (faqData.id) {
            await updateFaq({ id: faqData.id, data: payload }).unwrap();
            actionNotifier.success('FAQ updated successfully.');
            return;
        }

        await createFaq(payload).unwrap();
        actionNotifier.success('FAQ created successfully.');
    };

    const handleDeleteFaq = async (id) => {
        await deleteFaq(id).unwrap();
        actionNotifier.success('FAQ deleted successfully.');
    };

    const handleEditFaq = async (faq) => {
        try {
            const response = await fetchFaqById(faq.id).unwrap();
            return getFaqDetail(response) || faq;
        } catch (error) {
            actionNotifier.error(error?.data?.message || 'Unable to fetch FAQ details.');
            return faq;
        }
    };

    return (
        <>
            <Heading
                text="FAQ"
                path="#"
                onSearch={onSearch}
                search={search}
                isFilterShow={false}
                isGraphShow={false}
            />
            <SiteFaqContainer
                faqList={sectionFaqList}
                onSaveFaq={handleSaveFaq}
                onDeleteFaq={handleDeleteFaq}
                onEditFaq={handleEditFaq}
                refetchFaqList={refetchFaqList}
                search={search}
                selectedSection={selectedSection}
                sectionOptions={sectionOptions}
                setSelectedSection={setSelectedSection}
                isLoading={isCreatingFaq || isUpdatingFaq || isDeletingFaq || isFetchingFaqById}
            />
        </>
    );
};

export default SiteFaqPage;
